import React, { useEffect, useState } from "react";
import { repassarToqueParaBarra } from "../lib/camadas";
import { ArrowRight, ArrowLeft, X, Search, Shield, Calendar, MessageSquare, Package, DollarSign, Truck, FileText } from "lucide-react";

// Onboarding mostrado no primeiro login do usuário (por perfil).
// Sequência simples de 3 slides com ilustração + título + descrição.
// Skip ou conclusão grava `toalugando_onboarding_<userId>` no localStorage
// (uma vez por PESSOA; reaparece só depois de DIAS_DE_SILENCIO).

const STEPS_LOCATARIO = [
  {
    icon: Search,
    cor: "orange",
    titulo: "Encontre o que precisa",
    body: "Busque produtos, ferramentas, espaços e itens pra eventos. Filtre por categoria, distância e nível de verificação do locador.",
  },
  {
    icon: Shield,
    cor: "emerald",
    titulo: "Pagamento e identidade protegidos",
    body: "Sua identidade é validada por reconhecimento facial e o pagamento fica retido até a confirmação da entrega. Caução é devolvida quando o produto volta sem avarias.",
  },
  {
    icon: MessageSquare,
    cor: "cyan",
    titulo: "Tudo dentro do app",
    body: "Chat com o locador, rastreamento do entregador, contrato digital assinado e suporte — tudo em um lugar só. Em problemas, abra uma disputa e nossa mediação cuida.",
  },
];

const STEPS_LOCADOR = [
  {
    icon: Package,
    cor: "orange",
    titulo: "Anuncie seu produto",
    body: "Tire fotos, descreva o estado e defina o preço. A IA da Tô Alugando ajuda a estimar peso/dimensões pra calcular frete certo.",
  },
  {
    icon: Calendar,
    cor: "cyan",
    titulo: "Calendário e cupons",
    body: "Acompanhe sua agenda visual de locações. Crie cupons promocionais (ex: INVERNO20) pra divulgar nas suas redes e atrair mais clientes.",
  },
  {
    icon: DollarSign,
    cor: "emerald",
    titulo: "Comissão escalonada por nível",
    body: "Comissão de 5%. Com CNPJ, ela desce até 2% (Diamante) conforme volume e avaliações. Repasse direto na sua chave PIX. Exporta CSV pro contador a qualquer momento.",
  },
];

const STEPS_FRETEIRO = [
  {
    icon: Truck,
    cor: "indigo",
    titulo: "Receba entregas próximas",
    body: "Quando ficar online, recebe push de entregas no seu raio. Quanto mais próximo e melhor seu nível, mais cedo aparece o pedido.",
  },
  {
    icon: FileText,
    cor: "emerald",
    titulo: "Comissão e bônus por nível",
    body: "Comissão da plataforma escalona de 12% (Bronze) a 9% (Diamante). Acima do Bronze, ainda ganha bônus extra sobre o frete e repasse mais frequente.",
  },
  {
    icon: Shield,
    cor: "cyan",
    titulo: "Documentação e segurança",
    body: "Veículos passam por verificação (CRLV + fotos). Em rotas, GPS é compartilhado com o cliente e locador. Não combine pagamento por fora — anula a garantia da plataforma.",
  },
];

function stepsParaPerfil(perfil) {
  if (perfil === "locador") return STEPS_LOCADOR;
  if (perfil === "freteiro") return STEPS_FRETEIRO;
  return STEPS_LOCATARIO;
}

const CORES = {
  orange: { bg: "bg-orange-100", txt: "text-orange-600", btn: "bg-orange-600 hover:bg-orange-700" },
  emerald: { bg: "bg-emerald-100", txt: "text-emerald-600", btn: "bg-emerald-600 hover:bg-emerald-700" },
  cyan: { bg: "bg-cyan-100", txt: "text-cyan-600", btn: "bg-cyan-600 hover:bg-cyan-700" },
  indigo: { bg: "bg-indigo-100", txt: "text-indigo-600", btn: "bg-indigo-600 hover:bg-indigo-700" },
};

// Quanto tempo o tour fica quieto depois de visto/dispensado (dono, 23/09/2026).
// Uma vez só por pessoa (dono, 16/10/2026) — antes voltava a cada 180 dias.
const DIAS_DE_SILENCIO = Infinity;
// Um tour por sessão: ao entrar, o app começa como "locatario" e troca pro
// perfil real — antes isso disparava um tour atrás do outro, e a cada troca de
// perfil vinha tudo de novo. Era esse o "aparece toda vez".
const CHAVE_SESSAO = 'toalugando_onboarding_sessao';

export const OnboardingTour = ({ perfil, userId, onConcluir }) => {
  // Uma marcação POR PESSOA (não por perfil): ver o tour uma vez basta.
  const chave = `toalugando_onboarding_${userId || 'anon'}`;
  const [visivel, setVisivel] = useState(false);
  const [step, setStep] = useState(0);
  const steps = stepsParaPerfil(perfil);

  useEffect(() => {
    if (!userId) return;
    try {
      if (sessionStorage.getItem(CHAVE_SESSAO)) return;   // já apareceu nesta sessão
      const visto = localStorage.getItem(chave);
      if (visto) {
        const dias = (Date.now() - new Date(visto).getTime()) / 86400000;
        if (!Number.isFinite(dias) || dias < DIAS_DE_SILENCIO) return;
      }
      setVisivel(true);
    } catch (e) {
      // Sem armazenamento (aba anônima, app com dados limpos): mostra uma vez
      // e a trava de sessão abaixo impede a repetição enquanto o app estiver aberto.
      setVisivel(true);
    }
  }, [chave, userId]);

  // Registra a exibição assim que ele abre — some se a pessoa fechar o app no meio.
  useEffect(() => {
    if (!visivel) return;
    try { sessionStorage.setItem(CHAVE_SESSAO, '1'); } catch (e) {}
  }, [visivel]);

  // Outros avisos (o do CEP) esperam o tutorial fechar: um de cada vez.
  useEffect(() => {
    if (!visivel) return undefined;
    window.__tutorialAberto = true;
    return () => {
      window.__tutorialAberto = false;
      window.dispatchEvent(new Event('toalugando:tutorial:fechou'));
    };
  }, [visivel]);

  function concluir() {
    try { localStorage.setItem(chave, new Date().toISOString()); } catch (e) {}
    try { sessionStorage.setItem(CHAVE_SESSAO, '1'); } catch (e) {}
    setVisivel(false);
    onConcluir?.();
  }

  if (!visivel || !steps.length) return null;

  const atual = steps[step];
  const Icon = atual.icon;
  const c = CORES[atual.cor] || CORES.orange;
  const ehUltimo = step === steps.length - 1;

  return (
    <div className="fixed inset-0 z-[1200] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={(e) => { concluir(); repassarToqueParaBarra(e); }} />

      <div className="relative bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden">
        <button onClick={concluir} className="absolute top-3 right-3 w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center z-10">
          <X size={14} />
        </button>

        <div className={`${c.bg} px-6 pt-10 pb-6 flex flex-col items-center text-center`}>
          <div className="w-20 h-20 bg-white rounded-3xl flex items-center justify-center shadow-lg mb-4">
            <Icon size={36} className={c.txt} />
          </div>
          <h2 className="text-xl font-extrabold text-[#12192a] mb-2">{atual.titulo}</h2>
          <p className="text-sm text-slate-700 leading-relaxed max-w-sm">{atual.body}</p>
        </div>

        {/* Dots */}
        <div className="flex justify-center gap-2 py-4 bg-white">
          {steps.map((_, i) => (
            <div key={i}
              className={`h-2 rounded-full transition-all ${
                i === step ? `w-8 ${c.btn.split(" ")[0]}` : "w-2 bg-slate-200"
              }`} />
          ))}
        </div>

        <div className="flex gap-2 px-5 pb-5">
          {step > 0 && (
            <button
              onClick={() => setStep(step - 1)}
              className="flex-1 py-2.5 rounded-xl border border-slate-200 text-sm font-bold text-slate-700 hover:bg-slate-50 flex items-center justify-center gap-1"
            >
              <ArrowLeft size={14} /> Voltar
            </button>
          )}
          {!ehUltimo ? (
            <button
              onClick={() => setStep(step + 1)}
              className={`flex-1 py-2.5 rounded-xl text-white text-sm font-bold flex items-center justify-center gap-1 ${c.btn}`}
            >
              Próximo <ArrowRight size={14} />
            </button>
          ) : (
            <button
              onClick={concluir}
              className={`flex-1 py-2.5 rounded-xl text-white text-sm font-bold ${c.btn}`}
            >
              Vamos lá!
            </button>
          )}
        </div>

        <button
          onClick={concluir}
          className="w-full text-center text-[11px] text-slate-400 hover:text-slate-700 pb-3"
        >
          Pular tutorial
        </button>
      </div>
    </div>
  );
};
